import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion } from 'framer-motion';
import { 
  Building, 
  Users, 
  TrendingUp, 
  DollarSign,
  FileText,
  Play,
  BarChart3
} from 'lucide-react';

const CompanyForm = ({ companyId = null }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('company-info');
  const [formData, setFormData] = useState({
    // Company Info
    name: '',
    tagline: '',
    logo: '',
    website: '',
    founded: '',
    location: '',
    stage: 'seed',
    
    // Metrics
    metrics: {
      seeking: '',
      growth: '',
      users: '',
      arr: ''
    },
    
    // Team
    teamMembers: [
      { name: '', role: '', bio: '', initials: '', email: '' }
    ],
    
    // Pitch Deck
    pitchDeck: {
      uploaded: false,
      fileName: '',
      slides: 0,
      lastUpdated: null
    },
    
    // Financials
    financials: {
      year1: '',
      year2: '',
      year3: '',
      year4: '',
      year5: '',
      burnRate: '',
      cac: '',
      ltv: '',
      churn: '',
      breakeven: ''
    },
    
    // Due Diligence
    documents: [
      { name: 'Articles of Incorporation', uploaded: false, required: true },
      { name: 'Cap Table & Equity Structure', uploaded: false, required: true },
      { name: 'IP Portfolio & Patents', uploaded: false, required: true },
      { name: 'Legal Compliance Report', uploaded: false, required: false },
      { name: 'Insurance Documentation', uploaded: false, required: false },
      { name: 'Previous Funding History', uploaded: false, required: false },
      { name: 'Board Meeting Minutes', uploaded: false, required: false },
      { name: 'Customer References', uploaded: false, required: false }
    ],
    
    // Product Demo
    productDemo: {
      videoUploaded: false,
      liveDemo: '',
      features: [
        'AI-powered analytics dashboard',
        'Seamless integrations with 50+ tools',
        'Real-time reporting and insights',
        'Enterprise-grade security',
        'Mobile-responsive interface'
      ]
    }
  });

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!companyId) return;
      
      setLoading(true);
      try {
        const companyRef = doc(db, 'companies', companyId);
        const companyDoc = await getDoc(companyRef);
        
        if (companyDoc.exists()) {
          setFormData(companyDoc.data());
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [companyId]);

  const handleInputChange = (section, field, value) => {
    if (section === 'metrics' || section === 'financials') {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const companyData = {
        ...formData,
        userId: currentUser.uid,
        updatedAt: new Date().toISOString()
      };

      if (!companyId) {
        companyData.createdAt = new Date().toISOString();
      }

      const companyRef = doc(db, 'companies', companyId || crypto.randomUUID());
      await setDoc(companyRef, companyData, { merge: true });
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving company:', error);
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    { id: 'company-info', title: 'Company Info', icon: Building },
    { id: 'metrics', title: 'Key Metrics', icon: TrendingUp },
    { id: 'team', title: 'Team', icon: Users },
    { id: 'pitch-deck', title: 'Pitch Deck', icon: BarChart3 },
    { id: 'financials', title: 'Financials', icon: DollarSign },
    { id: 'due-diligence', title: 'Due Diligence', icon: FileText },
    { id: 'product-demo', title: 'Product Demo', icon: Play }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'company-info':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('company-info', 'name', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tagline</label>
              <input
                type="text"
                value={formData.tagline}
                onChange={(e) => handleInputChange('company-info', 'tagline', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Logo Initials</label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => handleInputChange('company-info', 'logo', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                maxLength={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('company-info', 'website', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Founded</label>
              <input
                type="date"
                value={formData.founded}
                onChange={(e) => handleInputChange('company-info', 'founded', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('company-info', 'location', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Stage</label>
              <select
                value={formData.stage}
                onChange={(e) => handleInputChange('company-info', 'stage', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="idea">Idea</option>
                <option value="pre-seed">Pre-seed</option>
                <option value="seed">Seed</option>
                <option value="series-a">Series A</option>
                <option value="series-b">Series B</option>
                <option value="growth">Growth</option>
              </select>
            </div>
          </div>
        );
      // Add other section renders here...
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-6">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              {companyId ? 'Edit Company' : 'Add New Company'}
            </h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          <div className="flex gap-6">
            {/* Sidebar */}
            <div className="w-64 space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      activeSection === section.id
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {section.title}
                  </button>
                );
              })}
            </div>

            {/* Main Content */}
            <div className="flex-1">
              <form onSubmit={handleSubmit}>
                {renderSection()}
                
                <div className="mt-8 flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 rounded-md hover:from-blue-600 hover:to-purple-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Company'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyForm; 